import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AlertTriangle, Bot, ClipboardList, Code, Copy, ExternalLink, HelpCircle, Key, Shield, Terminal, Zap } from 'lucide-react';
import { availableExampleTypes, availableLanguages, getCodeExample, CodeExample, codeExamples } from '../../../constants/apiExamples';
import { DocsPageHeader } from '../../../components/docs/DocsPageHeader';

export function OverviewIntro() {
  const { t } = useTranslation('docs');

  const quickstartSteps = [
    {
      title: t('overview.step1_title'),
      description: t('overview.step1_desc'),
      icon: Key,
    },
    {
      title: t('overview.step2_title'),
      description: t('overview.step2_desc'),
      icon: Terminal,
    },
    {
      title: t('overview.step3_title'),
      description: t('overview.step3_desc'),
      icon: Zap,
    },
  ];

  const resourceLinks = [
    {
      label: t('overview.res_openai_api'),
      href: 'https://platform.openai.com/docs/api-reference',
    },
    {
      label: t('overview.res_api_docs'),
      href: '/docs/api',
    },
    {
      label: t('overview.res_getting_started'),
      href: '/docs/quickstart',
    },
  ];
  return (
    <div className="space-y-16">
      <DocsPageHeader
        badge={t('overview.badge')}
        title={t('overview.heading')}
        description={t('overview.description')}
        titleSize="large"
      />

      <section className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {quickstartSteps.map(({ title, description, icon: Icon }) => (
          <div key={title} className="rounded-3xl border border-border bg-card p-6 shadow-sm">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/20 text-primary text-white">
              <Icon className="h-6 w-6" />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-foreground">{title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{description}</p>
          </div>
        ))}
      </section>

      <section className="space-y-4">
        <h3 className="text-2xl font-semibold text-foreground">{t('overview.further_reading')}</h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {resourceLinks.map(({ label, href }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-between rounded-2xl border border-border bg-card px-4 py-3 text-sm font-semibold text-primary transition hover:border-indigo-200 hover:text-indigo-500"
            >
              <span>{label}</span>
              <ExternalLink className="h-4 w-4" />
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}

export function ExamplesContent() {
  const { t } = useTranslation(['docs', 'settings', 'common']);
  const [activeLanguageTab, setActiveLanguageTab] = useState<keyof CodeExample>('nodejs');
  const [activeExampleTab, setActiveExampleTab] = useState<(typeof availableExampleTypes)[number]>('basic');

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common:copied_to_clipboard'));
    } catch (error) {
      // Fallback for older browsers or insecure contexts
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success(t('common:copied_to_clipboard'));
      } catch (fallbackError) {
        toast.error(t('common:failed_to_copy'));
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="space-y-16">
      <DocsPageHeader
        badge={t('code_examples.badge')}
        title={t('code_examples.heading')}
        description={t('code_examples.description')}
      />

      {/* Code Examples */}
      <div className="rounded-xl border border-border bg-card/50 backdrop-blur-sm shadow-sm overflow-hidden">
        {/* Header */}
        <div className="p-5 border-b border-border">
          <div className="flex items-center gap-3.5">
            <div className="p-2.5 rounded-lg bg-primary/10">
              <Code className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="text-[15px] font-semibold text-foreground tracking-[-0.02em]">
                {t('settings:api_keys.examples_title')}
              </h3>
              <p className="text-[13px] text-muted-foreground mt-0.5">
                {t('settings:api_keys.examples_subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Language Tabs */}
        <div className="px-5 pt-5">
          <div className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-3">
            {t('settings:api_keys.language')}
          </div>
          <div className="inline-flex items-center bg-muted p-1 rounded-xl border border-border/50 mb-5">
              <button
                onClick={() => setActiveLanguageTab('curl')}
                className={`flex items-center gap-2 py-1.5 px-4 rounded-lg text-[13px] font-medium transition-all ${
                  activeLanguageTab === 'curl'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                }`}
              >
                <div className="w-5 h-5 bg-slate-500/20 rounded flex items-center justify-center">
                  <span className="text-slate-300 font-bold text-[8px]">C</span>
                </div>
                cURL
              </button>
            <button
              onClick={() => setActiveLanguageTab('nodejs')}
              className={`flex items-center gap-2 py-1.5 px-4 rounded-lg text-[13px] font-medium transition-all ${
                activeLanguageTab === 'nodejs'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
              }`}
            >
              <div className="w-5 h-5 bg-green-500/20 rounded flex items-center justify-center">
                <span className="text-green-500 font-bold text-[9px]">{t('common:js')}</span>
              </div>
              Node.js
            </button>
            <button
              onClick={() => setActiveLanguageTab('typescript')}
              className={`flex items-center gap-2 py-1.5 px-4 rounded-lg text-[13px] font-medium transition-all ${
                activeLanguageTab === 'typescript'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
              }`}
            >
              <div className="w-5 h-5 bg-blue-500/20 rounded flex items-center justify-center">
                <span className="text-blue-500 font-bold text-[9px]">{t('common:ts')}</span>
              </div>
              TypeScript
            </button>
            <button
              onClick={() => setActiveLanguageTab('python')}
              className={`flex items-center gap-2 py-1.5 px-4 rounded-lg text-[13px] font-medium transition-all ${
                activeLanguageTab === 'python'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
              }`}
            >
              <div className="w-5 h-5 bg-yellow-500/20 rounded flex items-center justify-center">
                <span className="text-yellow-500 font-bold text-[9px]">{t('common:py')}</span>
              </div>
              Python
            </button>
            <button
              onClick={() => setActiveLanguageTab('python_sdk')}
              className={`flex items-center gap-2 py-1.5 px-4 rounded-lg text-[13px] font-medium transition-all ${
                activeLanguageTab === 'python_sdk'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
              }`}
            >
              <div className="w-5 h-5 bg-emerald-500/20 rounded flex items-center justify-center">
                <span className="text-emerald-500 font-bold text-[8px]">{t('common:ai')}</span>
              </div>
              {t('settings:api_keys.python_sdk')}
            </button>
          </div>
        </div>

        {/* Example Type Tabs */}
        <div className="px-5 pb-5">
          <div className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-3">
            {t('settings:api_keys.implementation')}
          </div>
          <div className="inline-flex items-center bg-muted p-1 rounded-xl border border-border/50 mb-5">
            <button
              onClick={() => setActiveExampleTab('basic')}
              className={`flex items-center gap-1.5 py-1.5 px-3.5 rounded-lg text-[13px] font-medium transition-all ${
                activeExampleTab === 'basic'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
              }`}
            >
              💬 {t('settings:api_keys.basic_chat')}
            </button>
            <button
              onClick={() => setActiveExampleTab('streaming')}
              className={`flex items-center gap-1.5 py-1.5 px-3.5 rounded-lg text-[13px] font-medium transition-all ${
                activeExampleTab === 'streaming'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
              }`}
            >
              ⚡ {t('settings:api_keys.streaming')}
            </button>
            <button
              onClick={() => setActiveExampleTab('conversation')}
              className={`flex items-center gap-1.5 py-1.5 px-3.5 rounded-lg text-[13px] font-medium transition-all ${
                activeExampleTab === 'conversation'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
              }`}
            >
              🗣️ {t('settings:api_keys.conversation_tab')}
            </button>
          </div>
        </div>

        {/* Code Content */}
        <div className="border-t border-border">
          <div className="p-5">
            <div className="bg-zinc-950 rounded-xl overflow-hidden border border-zinc-800">
              {/* Code Header */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-zinc-900 border-b border-zinc-800">
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-[#ff5f57]"></div>
                    <div className="w-3 h-3 rounded-full bg-[#febc2e]"></div>
                    <div className="w-3 h-3 rounded-full bg-[#28c840]"></div>
                  </div>
                  <span className="text-[13px] font-medium text-zinc-400 ml-1">
                    {activeLanguageTab === 'curl' ? 'cURL' :
                     activeLanguageTab === 'nodejs' ? 'Node.js' :
                     activeLanguageTab === 'typescript' ? 'TypeScript' :
                     activeLanguageTab === 'python_sdk' ? 'Python (OpenAI SDK)' : 'Python'} — {activeExampleTab === 'basic' ? t('settings:api_keys.basic_chat') :
                      activeExampleTab === 'streaming' ? t('settings:api_keys.streaming') : t('settings:api_keys.conversation_tab')}
                  </span>
                </div>
                <button
                  onClick={() => {
                    const code = codeExamples[activeLanguageTab]?.[activeExampleTab];
                    if (code) copyToClipboard(code);
                  }}
                  className="text-zinc-500 hover:text-zinc-300 transition-colors p-1"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Code Block */}
              <pre className="text-zinc-200 p-5 text-sm overflow-x-auto font-mono leading-relaxed antialiased">
                {codeExamples[activeLanguageTab]?.[activeExampleTab] || t('settings:api_keys.example_not_found')}
              </pre>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Notes */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950 dark:to-orange-950 border border-amber-200 dark:border-amber-800 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center shadow-md">
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-100 mb-3">
                {t('code_examples.important_notes')}
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-200 dark:bg-amber-800 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-amber-800 dark:text-amber-200 text-xs font-bold">1</span>
                  </div>
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    {t('code_examples.note_api_key')}
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-200 dark:bg-amber-800 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-amber-800 dark:text-amber-200 text-xs font-bold">2</span>
                  </div>
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    {t('code_examples.note_base_url')}
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-200 dark:bg-amber-800 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-amber-800 dark:text-amber-200 text-xs font-bold">3</span>
                  </div>
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    {t('code_examples.note_auth')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


